import { Suspense, lazy, useEffect, useState } from "react";

import {
  Routes,
  Route,
  useNavigate,
  Navigate,
  useLocation,
} from "react-router-dom";
import { toast } from "sonner";

import AuthModal from "./components/AuthModal.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import MobileNavBar from "./components/MobileNavBar";
import Sidebar from "./components/Sidebar.jsx";
import Breadcrumbs from "./components/ui/Breadcrumbs.jsx";
import { Toaster } from "./components/ui/sonner";
import { useMobile } from "./hooks/useMobile";
import { useNotifications } from "./hooks/useNotifications.js";
import { useUser } from "./hooks/useUser.js";
import { supabase } from "./lib/supabase";

// Helper: Lazy load with prefetch capability
const lazyWithPrefetch = (importFn) => {
  const Component = lazy(importFn);
  Component.preload = importFn;
  return Component;
};

const AdminView = lazyWithPrefetch(() => import("./views/AdminView.jsx"));
const BuilderView = lazyWithPrefetch(() => import("./views/BuilderView.jsx"));
const ProfileView = lazyWithPrefetch(() => import("./views/ProfileView.jsx"));
const SavedView = lazyWithPrefetch(() => import("./views/SavedView.jsx"));
const PromptFeedView = lazyWithPrefetch(
  () => import("./views/PromptFeedView.jsx"),
);

const RouteLoadingFallback = () => (
  <div className="flex h-full min-h-[40vh] items-center justify-center">
    <div className="flex items-center gap-2 text-sm text-slate-400">
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-500/50 border-t-indigo-500" />
    </div>
  </div>
);

const StudioAliasRedirect = () => {
  const location = useLocation();
  return (
    <Navigate to={{ pathname: "/library", search: location.search }} replace />
  );
};

const App = () => {
  // --- STATE ---
  // The useUser hook now handles ALL auth logic internally.
  const { user, loading, refreshUser } = useUser();

  const [_sessionHistory, setSessionHistory] = useState([]);
  const [promptToLoad, setPromptToLoad] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const notificationsHook = useNotifications(user);

  // --- THEME INITIALIZATION ---
  // Load saved theme from local storage on boot to avoid "flash" of default theme.
  // If no local theme exists, fetch the admin-set site default from Supabase.
  useEffect(() => {
    const savedTheme = localStorage.getItem("cmp-theme");
    const savedMode = localStorage.getItem("cmp-theme-mode");
    const root = document.documentElement;

    // Helper: apply a theme object + mode to the document
    const applyTheme = (themeObj, mode) => {
      if (mode === "dark") {
        root.classList.add("dark");
      } else if (mode === "light") {
        root.classList.remove("dark");
      }
      Object.entries(themeObj).forEach(([key, value]) => {
        if (key === "label") return;
        const cssVar = `--${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
        const cssValue = key === "radius" ? `${value}rem` : `hsl(${value})`;
        root.style.setProperty(cssVar, cssValue);
      });
    };

    if (savedTheme) {
      // User has a personal theme override — use it
      if (savedMode === "dark") {
        root.classList.add("dark");
      } else if (savedMode === "light") {
        root.classList.remove("dark");
      }
      try {
        applyTheme(JSON.parse(savedTheme), savedMode);
      } catch (e) {
        console.error("Failed to parse saved theme", e);
        localStorage.removeItem("cmp-theme");
      }
    } else {
      // No local theme — try fetching the site default from Supabase
      if (savedMode === "dark") {
        root.classList.add("dark");
      } else if (savedMode === "light") {
        root.classList.remove("dark");
      } else if (
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      ) {
        root.classList.add("dark");
      }

      supabase
        .from("themes")
        .select("config, mode")
        .eq("is_site_default", true)
        .single()
        .then(({ data }) => {
          if (data?.config) {
            applyTheme(data.config, data.mode);
          }
        })
        .catch(() => {
          // No site default set — keep CSS defaults from index.css
        });
    }
  }, []);

  // --- HELPERS ---
  const showToast = (msg, type = "success") => {
    if (type === "error") {
      toast.error(msg);
    } else if (type === "success") {
      toast.success(msg);
    } else {
      toast(msg);
    }
  };

  const addToHistory = (prompt, type) => {
    setSessionHistory((prev) =>
      [
        { prompt, type, timestamp: new Date().toLocaleTimeString() },
        ...prev,
      ].slice(0, 50),
    );
  };

  const loadPrompt = (data) => {
    setPromptToLoad(data);
    navigate("/");
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      showToast("Logged out successfully.");
      // The useUser hook will detect the change and update 'user' to null automatically
      navigate("/");
    } catch {
      showToast("Logout failed", "error");
    }
  };

  const handleLoginRequest = () => {
    setShowLoginModal(true);
  };

  // Optimized: Prefetch commonly used routes after initial render
  useEffect(() => {
    // Prefetch critical routes when browser is idle
    const prefetchRoutes = () => {
      SavedView.preload?.();
      PromptFeedView.preload?.();
      ProfileView.preload?.();
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(prefetchRoutes, { timeout: 5000 });
    } else {
      setTimeout(prefetchRoutes, 2000);
    }
  }, []);

  // --- RENDER ---
  // Optimized: Don't block the entire app during auth initialization
  // Show a subtle skeleton while loading, but render the layout immediately

  return (
    <ErrorBoundary>
      <div className="flex w-full h-dvh bg-theme-bg font-sans text-slate-900 dark:text-slate-100 overflow-hidden transition-colors duration-200">
        {/* Sidebar gets the 'user' object directly from the hook */}
        <Sidebar
          handleLogin={handleLoginRequest}
          handleLogout={handleLogout}
          user={user}
          notifications={notificationsHook}
        />

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative pt-[calc(3.5rem+env(safe-area-inset-top,0px))] md:pt-0">
          <div className="hidden md:block px-4 pt-0 shrink-0">
            <Breadcrumbs />
          </div>

          <div className="flex-1 min-h-0 relative overflow-hidden flex flex-col pb-[calc(4rem+env(safe-area-inset-bottom,0px))] md:pb-0">
            <Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <BuilderView
                      user={user}
                      initialData={promptToLoad}
                      clearInitialData={() => setPromptToLoad(null)}
                      showToast={showToast}
                      addToHistory={addToHistory}
                      onLoginRequest={handleLoginRequest}
                    />
                  }
                />
                <Route
                  path="/feed"
                  element={
                    <PromptFeedView
                      loadPrompt={loadPrompt}
                      showToast={showToast}
                    />
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProfileView user={user} refreshUser={refreshUser} />
                  }
                />
                <Route
                  path="/admin"
                  element={<AdminView user={user} loading={loading} />}
                />
                <Route path="/studio" element={<StudioAliasRedirect />} />
                <Route
                  path="/library"
                  element={
                    user ? (
                      <SavedView
                        user={user}
                        loadPrompt={loadPrompt}
                        showToast={showToast}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center p-8">
                          <h2 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-2">
                            Library is locked
                          </h2>
                          <p className="text-slate-500 dark:text-slate-400 mb-4">
                            Please sign in to save your prompts.
                          </p>
                          <button
                            onClick={handleLoginRequest}
                            className="min-h-11 rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
                          >
                            Sign In
                          </button>
                        </div>
                      </div>
                    )
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </div>
        </div>

        {/* Mobile Bottom Navigation - only shows on phones */}
        {isMobile && <MobileNavBar user={user} />}

        <AuthModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          showToast={showToast}
        />

        <Toaster />
      </div>
    </ErrorBoundary>
  );
};

export default App;
